import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, ArrowRightIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { WhatsAppFloat } from '../components/WhatsAppFloat';
import { useLanguage } from '../i18n/LanguageContext';
type Category = 'all' | 'web' | 'servicios' | 'identidad';
interface Project {
  video: string;
  poster?: string;
  badge: string;
  client: string;
  industry: string;
  industryEn: string;
  description: string;
  descriptionEn: string;
  category: Exclude<Category, 'all'>;
}
const projects: Project[] = [{
  video: 'https://cdn.magicpatterns.com/uploads/fDKSQgTvvdYZ4BKZv2d4rv/ARCTIC.mp4',
  badge: 'Diseño Web',
  client: 'Arctic Breeze',
  industry: 'Reparación de Aires Acondicionados · Miami, FL',
  industryEn: 'Air Conditioning Repair · Miami, FL',
  description: 'Presencia digital profesional para empresa de HVAC — diseñada para generar confianza y captar clientes desde el primer clic.',
  descriptionEn: 'Professional digital presence for an HVAC company — designed to build trust and capture clients from the first click.',
  category: 'web'
}, {
  video: '/videos/LifeLab.mp4',
  badge: 'Diseño Web',
  client: 'LifeLab',
  industry: 'Seguros de Vida · USA',
  industryEn: 'Life Insurance · USA',
  description: 'Web estratégica para agencia de seguros — con mensaje claro, estructura de confianza y sistema de captación de leads.',
  descriptionEn: 'Strategic website for an insurance agency — with a clear message, a trust-building structure, and a lead capture system.',
  category: 'web'
}, {
  video: 'https://cdn.magicpatterns.com/uploads/kEChVgZzmLFcZicUnPCJtt/MDA WEB.mp4',
  badge: 'Diseño Web',
  client: 'MDA Consulting Solutions',
  industry: 'Ingeniería y Manufactura · USA',
  industryEn: 'Engineering & Manufacturing · USA',
  description: 'Sitio corporativo para empresa de soluciones de ingeniería — posicionamiento profesional para el mercado B2B.',
  descriptionEn: 'Corporate site for an engineering solutions company — professional positioning for the B2B market.',
  category: 'web'
}, {
  video: 'https://cdn.magicpatterns.com/uploads/nGHNZtShCzyYpnYDrPVHUU/0622 (2).mp4',
  badge: 'Diseño Web',
  client: 'Apex Roofing',
  industry: 'Reparación de Techos · USA',
  industryEn: 'Roofing Repair · USA',
  description: 'Presencia digital enfocada en conversión para empresa de roofing — diseñada para generar llamadas y solicitudes de cotización.',
  descriptionEn: 'Conversion-focused digital presence for a roofing company — designed to drive calls and quote requests.',
  category: 'web'
}];
const filters: {
  id: Category;
  label: string;
  labelEn: string;
}[] = [{
  id: 'all',
  label: 'Todos',
  labelEn: 'All'
}, {
  id: 'web',
  label: 'Web Corporativa',
  labelEn: 'Corporate Web'
}, {
  id: 'servicios',
  label: 'Servicios',
  labelEn: 'Services'
}, {
  id: 'identidad',
  label: 'Identidad Digital',
  labelEn: 'Digital Identity'
}];
function ProjectCard({
  project,
  index
}: {
  project: Project;
  index: number;
}) {
  const {
    lang
  } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
    setPlaying(true);
  };
  const stop = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    setPlaying(false);
  };
  return <motion.article initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    ease: 'easeOut',
    delay: index * 0.1
  }} onMouseEnter={play} onMouseLeave={stop} onClick={() => playing ? stop() : play()} className="group cursor-pointer bg-white rounded-[20px] overflow-hidden shadow-[0_8px_32px_rgba(90,69,121,0.1)] transition-all duration-[250ms] ease-in-out hover:-translate-y-1.5 hover:shadow-[0_20px_48px_rgba(90,69,121,0.18)] flex flex-col" data-id="element-544">
      {/* Video / poster area */}
      <div className="relative h-[220px] lg:h-[280px] bg-[#1A1A2E] overflow-hidden" data-id="element-545">
        <video ref={videoRef} src={project.video} poster={project.poster} muted loop playsInline preload="metadata" className="w-full h-full object-cover" data-id="element-546" />
        {/* Play overlay */}
        <div className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? 'opacity-0' : 'opacity-100'}`} data-id="element-547">
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-white/90 shadow-lg" data-id="element-548">
            <PlayIcon className="w-5 h-5 text-[#5A4579] ml-0.5" fill="#5A4579" data-id="element-549" />
          </span>
        </div>
        {/* Bottom gradient for readability */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.4),transparent)]" data-id="element-550" />
      </div>

      {/* Body */}
      <div className="p-5 lg:p-6 flex flex-col flex-1" data-id="element-551">
        <span className="self-start inline-block bg-[#F0EDFB] text-[#5A4579] text-[11px] font-semibold tracking-[1px] rounded-[100px] px-3 py-1 mb-4" data-id="element-552">
          {project.badge}
        </span>

        <h3 className="font-display font-bold text-[22px] text-[#1A1A2E] leading-tight" data-id="element-553">
          {project.client}
        </h3>
        <p className="text-[13px] text-[#6B7280] mt-1" data-id="element-554">
          {lang === 'es' ? project.industry : project.industryEn}
        </p>
        <p className="text-[14px] text-[#4B5563] leading-[1.6] mt-3 flex-1" data-id="element-555">
          {lang === 'es' ? project.description : project.descriptionEn}
        </p>

        {/* Footer */}
        <div className="border-t border-[#F0EDFB] pt-4 mt-5" data-id="element-556">
          <span className="group/link inline-flex items-center gap-1.5 text-[#5A4579] text-[13px] font-semibold" data-id="element-557">
            {lang === 'es' ? 'Ver proyecto' : 'View project'}
            <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" data-id="element-558" />
          </span>
        </div>
      </div>
    </motion.article>;
}
export function PortfolioPage() {
  const {
    lang
  } = useLanguage();
  const [active, setActive] = useState<Category>('all');
  const visible = active === 'all' ? projects : projects.filter(p => p.category === active);
  return <div className="min-h-screen bg-inkova-cream font-sans selection:bg-inkova-accent/30 selection:text-inkova-primary" data-id="element-559">
      <Navbar data-id="element-560" />
      <main className="pt-24 lg:pt-28" data-id="element-561">
        {/* ─────────────── HERO ─────────────── */}
        <section className="bg-[#FAF8F5] pt-12 lg:pt-20 pb-12 lg:pb-[60px] text-center px-6" data-id="element-562">
          <motion.span initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} className="block font-sans text-[10px] font-bold tracking-[3px] uppercase text-[#5A4579] mb-5" data-id="element-563">
            {lang === 'es' ? 'NUESTRO TRABAJO' : 'OUR WORK'}
          </motion.span>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut',
          delay: 0.1
        }} className="font-display text-[30px] sm:text-[40px] lg:text-[52px] text-[#1A1A2E] leading-[1.1] tracking-[-0.5px] max-w-[820px] mx-auto" data-id="element-564">
            {lang === 'es' ? <>
                Proyectos que ya están generando{' '}
                <em className="italic text-[#5A4579]" data-id="element-565">resultados.</em>
              </> : <>
                Projects already generating{' '}
                <em className="italic text-[#5A4579]" data-id="element-566">results.</em>
              </>}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut',
          delay: 0.2
        }} className="text-[16px] text-[#6B7280] max-w-[560px] mx-auto mt-5 leading-[1.7]" data-id="element-567">
            {lang === 'es' ? 'Cada uno construido desde la identidad de quien lo creó.' : 'Each one built from the identity of the person who created it.'}
          </motion.p>
        </section>

        {/* ─────────────── FILTER BAR ─────────────── */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8 mb-10 lg:mb-12" data-id="element-568">
          <div className="flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden justify-start lg:justify-center" data-id="element-569">
            {filters.map(f => {
            const isActive = active === f.id;
            return <button key={f.id} type="button" onClick={() => setActive(f.id)} className={`shrink-0 rounded-[100px] px-5 py-2 text-[13px] font-semibold transition-all duration-200 ${isActive ? 'bg-[#5A4579] text-white border border-[#5A4579]' : 'bg-transparent text-[#5A4579] border border-[#5A4579] hover:bg-[#F0EDFB]'}`} data-id="element-570">
                  {lang === 'es' ? f.label : f.labelEn}
                </button>;
          })}
          </div>
        </div>

        {/* ─────────────── PROJECTS GRID ─────────────── */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8" data-id="element-571">
          <AnimatePresence mode="wait" data-id="element-572">
            <motion.div key={active} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-573">
              {visible.length > 0 ? visible.map((p, i) => <ProjectCard key={p.client} project={p} index={i} data-id="element-574" />) : <p className="col-span-full text-center text-[#6B7280] py-16" data-id="element-575">
                  {lang === 'es' ? 'Pronto más proyectos en esta categoría.' : 'More projects in this category coming soon.'}
                </p>}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ─────────────── BOTTOM CTA ─────────────── */}
        <div className="px-0 lg:px-6" data-id="element-576">
          <section className="bg-[linear-gradient(135deg,#2E1F47,#5A4579)] text-center text-white rounded-none lg:rounded-[24px] py-10 px-6 lg:py-[60px] lg:px-10 my-14 lg:my-20 max-w-[900px] mx-auto" data-id="element-577">
            <span className="block text-[10px] font-bold tracking-[3px] uppercase text-white/70 mb-5" data-id="element-578">
              {lang === 'es' ? '¿TU NEGOCIO AQUÍ?' : 'YOUR BUSINESS HERE?'}
            </span>
            <h2 className="font-display text-[28px] lg:text-[36px] leading-[1.15] max-w-[640px] mx-auto mb-4" data-id="element-579">
              {lang === 'es' ? 'Tu proyecto merece una presencia que lo represente.' : 'Your project deserves a presence that represents it.'}
            </h2>
            <p className="text-[16px] text-white/75 mb-8" data-id="element-580">
              {lang === 'es' ? 'Hablemos y construyamos algo tuyo.' : "Let's talk and build something that's yours."}
            </p>
            <div className="flex flex-col items-center gap-5" data-id="element-581">
              <div className="flex flex-col items-center" data-id="element-582">
                <a href="https://calendly.com/inkovacreation" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-white text-[#5A4579] font-sans font-semibold text-[14px] tracking-[0.6px] uppercase px-10 py-4 rounded-[100px] transition-all duration-200 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(0,0,0,0.2)]" data-id="element-583">
                  {lang === 'es' ? 'Da el primer paso' : 'Take the first step'}
                </a>
                <span className="text-[12px] text-white/60 mt-3" data-id="element-584">
                  {lang === 'es' ? 'Sesión Diagnóstico · $50 · 30 minutos' : 'Diagnostic Session · $50 · 30 minutes'}
                </span>
              </div>
              <a href="https://wa.me/13054781618" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 font-sans text-[14px] text-white border-[1.5px] border-white/60 rounded-[100px] px-6 py-3 transition-all duration-200 hover:bg-white/10 hover:border-white" data-id="element-585">
                <span data-id="element-586">
                  {lang === 'es' ? 'Tengo una pregunta' : 'I have a question'}
                </span>
                <span className="transition-transform duration-200 group-hover:translate-x-[3px]" data-id="element-587">
                  → WhatsApp
                </span>
              </a>
            </div>
          </section>
        </div>
      </main>
      <Footer data-id="element-588" />
      <WhatsAppFloat data-id="element-589" />
    </div>;
}